import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChildren,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';
import { TooltipDirective } from '../../../tooltip/tooltip.directive';
import { TitleIfTruncatedDirective } from '../../card/title-if-truncated.directive';
import { NavChildCloseEvent, NavChildItem, NavChildStatus, NavItem, NavSection } from './nav.model';

let nextNavId = 0;

/** Current-page matching: query strings (paging, sorting, filters) never matter. */
const EXACT: IsActiveMatchOptions = { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' };
const SUBSET: IsActiveMatchOptions = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' };

/** Outline marks for the statuses other than the ring, as 24×24 path data. */
const STATUS_ICONS: Record<Exclude<NavChildStatus['kind'], 'progress'>, readonly string[]> = {
  waiting: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7.5V12l3 2'],
  done: ['m5 12.5 4.5 4.5L19 7'],
  error: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 8v4.5', 'M12 16h.01'],
};

/** A close button was activated while it had focus; its item may or may not actually close. */
interface PendingFocus {
  parentId: string;
  childId: string;
  /** The visible child ids, in order, at the moment the button was activated. */
  order: readonly string[];
}

/**
 * The cabinet rail's link list: headed groups of route links (the current
 * page carries aria-current) or action buttons. While collapsed, labels stay
 * in the accessibility tree and show as a tooltip on hover and focus.
 */
@Component({
  selector: 'app-nav-cabinet',
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, TooltipDirective, TitleIfTruncatedDirective],
  host: {
    '[class.collapsed]': 'collapsed()',
  },
})
export class NavCabinetComponent {
  readonly sections = input<readonly NavSection[]>([]);
  readonly collapsed = input(false);

  /** A route link was activated. */
  readonly navigate = output<NavItem>();
  /** An action item was activated; carries its `action`. */
  readonly itemAction = output<string>();
  /** A child's close button was activated. */
  readonly childClose = output<NavChildCloseEvent>();

  /** Children always match their exact route. */
  readonly childMatch = EXACT;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private pendingFocus: PendingFocus | null = null;

  private readonly tooltips = viewChildren(TooltipDirective);
  private readonly uid = `nav-${nextNavId++}`;

  constructor() {
    // Runs whenever the data changes (a close completing, or being cancelled).
    // Everything read inside is untracked, so this reacts only to `sections()`.
    effect(() => {
      const sections = this.sections();
      untracked(() => this.checkPendingFocus(sections));
    });
  }

  headingId(section: NavSection): string {
    return `${this.uid}-${section.id}`;
  }

  childrenLabelId(item: NavItem): string {
    return `${this.uid}-children-${item.id}`;
  }

  /** "Open project" plus the child's type, e.g. "Open project · SoftJaws", when both are set. */
  childTooltipCaption(item: NavItem, child: NavChildItem): string | undefined {
    const caption = item.childTooltipCaption;
    const typeLabel = child.tag?.typeLabel;
    if (caption && typeLabel) return `${caption} · ${typeLabel}`;
    return caption ?? (typeLabel ? typeLabel : undefined);
  }

  /** A tooltip with more than the name: a caption, a preview or a status. */
  hasRichTooltip(item: NavItem, child: NavChildItem): boolean {
    return !!(child.previewUrl || child.status || this.childTooltipCaption(item, child));
  }

  /** The ring's filled share of its 100-unit length; a spinning quarter while the percent is unknown. */
  ringDash(status: NavChildStatus): string {
    const percent = status.percent;
    if (percent == null) return '25 100';
    // A sliver even at 0%, so a job that just started still reads as running.
    return `${Math.min(100, Math.max(4, percent))} 100`;
  }

  statusIcon(status: NavChildStatus): readonly string[] {
    return status.kind === 'progress' ? [] : STATUS_ICONS[status.kind];
  }

  matchOptions(item: NavItem): IsActiveMatchOptions {
    return item.exact === false ? SUBSET : EXACT;
  }

  hasBadge(item: NavItem): boolean {
    return (item.badge ?? 0) > 0;
  }

  badgeText(item: NavItem): string {
    const count = item.badge ?? 0;
    return count > 99 ? '99+' : String(count);
  }

  onAction(item: NavItem): void {
    if (item.action) this.itemAction.emit(item.action);
  }

  childCount(item: NavItem): number {
    return item.children?.length ?? 0;
  }

  closeChild(item: NavItem, child: NavChildItem, event: Event): void {
    const button = event.currentTarget as HTMLElement;
    const root = this.host.nativeElement.shadowRoot;
    if (root && root.activeElement === button) {
      this.pendingFocus = { parentId: item.id, childId: child.id, order: (item.children ?? []).map((entry) => entry.id) };
    }
    this.childClose.emit({ parentId: item.id, childId: child.id });
  }

  /** Escape hides a collapsed item's tooltip without moving focus. */
  hideTooltips(event: Event): void {
    for (const tip of this.tooltips()) tip.onMouseLeave(event);
  }

  /** Moves focus on once a close started from a focused close button has actually gone through. */
  private checkPendingFocus(sections: readonly NavSection[]): void {
    const pending = this.pendingFocus;
    if (!pending) return;
    const item = sections.flatMap((section) => section.items).find((entry) => entry.id === pending.parentId);
    const stillOpen = item?.children?.some((child) => child.id === pending.childId) ?? false;
    if (stillOpen) return;
    this.pendingFocus = null;
    this.moveFocusAfterClose(pending, item);
  }

  /** The next remaining child, else the previous one, else the parent item's own link or button. */
  private moveFocusAfterClose(pending: PendingFocus, item: NavItem | undefined): void {
    const root = this.host.nativeElement.shadowRoot;
    if (!root) return;
    const remaining = new Set((item?.children ?? []).map((child) => child.id));
    const index = pending.order.indexOf(pending.childId);
    const targetId = [pending.order[index + 1], pending.order[index - 1]].find((id): id is string => !!id && remaining.has(id));
    const selector = targetId
      ? this.collapsed()
        ? `a.nav-child-link[data-child-id="${targetId}"]`
        : `button.nav-child-close[data-child-id="${targetId}"]`
      : `[data-item-id="${pending.parentId}"]`;
    root.querySelector<HTMLElement>(selector)?.focus();
  }
}
